import { ConcurrencySweepChart, CostComparisonChart, VerticalIntegrationChart } from '@/components/charts';
import type { Block, DocSection } from '@/components/investor';

/**
 * DialogMe investor overview. Mirrors docs/investors/investors.md; every figure
 * comes from docs/business-plan/dialogme-business-plan.md — keep the three in sync.
 */

export const investorsStatus =
  'Open beta · Deployed multi-tenant platform · Zero paying customers · Self-funded on ≈€23,000';

export const investorsSummary =
  '**A managed agentic platform that automates a business’s customer communication end to end — answer, show, book, ticket, pay — sold by subscription to small and medium businesses, on inference the company owns rather than rents.** DialogMe runs on its own AI stack, [Fnk](https://fnk.ai). This page is about the platform; the stack, the benchmark and private deployment are documented at fnk.ai. Every financial figure is a management scenario, not contracted revenue.';

export const investorsTop: Block[] = [
  {
    kind: 'matrix',
    head: ['', 'DialogMe — the platform', 'Fnk — our own stack'],
    rows: [
      ['What it is', 'The managed conversational platform for businesses', 'The AI stack, the Harness and inference'],
      ['Sells', 'Monthly subscriptions — Free, Starter €39, Scale €299, Custom', 'Stack licence per GPU, partial and full box, paid assessments'],
      ['Buyers', 'Small and medium businesses', 'Enterprises, inference providers, data centres, OEMs'],
      ['Relationship', 'Runs on Fnk — its own inference, not a per-token API', 'The stack DialogMe is built on; DialogMe is its reference production workload'],
      ['Site', '[dialogme.ai](https://dialogme.ai)', '[fnk.ai](https://fnk.ai)'],
    ],
    minWidth: '600px',
  },
  {
    kind: 'text',
    text: 'A DialogMe account that outgrows the subscription — a data-residency rule, an audit, a scale problem — moves to a private deployment of our own stack, Fnk, and keeps its configuration. The DialogMe plan does not count that as subscription revenue.',
  },
];

export const investorsSections: DocSection[] = [
  {
    title: 'The thesis',
    blocks: [
      {
        kind: 'text',
        text: 'Customers stopped wanting to click. They want to state what they need — find it, book it, order it, raise a ticket — and increasingly they delegate the doing to an AI assistant. What a small business can offer them has not moved: a bot that answers FAQs, then hands over the moment there is money involved. The agents that genuinely act — Sierra, Decagon, Fin — exist, and they are priced for enterprises: $0.50–$2.00 per resolution or six-figure contracts, on rented frontier models.',
      },
      {
        kind: 'callout',
        title: 'The real blocker',
        text: '**It is not the agent. It is everything the agent has to reach**: the messaging channel, the catalog, the calendar, the ticketing system and the payment provider — each a separate integration project, each maintained forever. That is where these efforts die, and why almost no small business has automated customer communication end to end.',
      },
      {
        kind: 'cards',
        cols: 2,
        items: [
          { title: 'The technical bet is settled, one layer down', text: 'The Fnk stack was measured against the assembled open-source alternative on an NVIDIA GPU and won at every tested concurrency, by **+36% to +104%**, with **2.04×** the completed requests per second. The same GPU serves about twice the conversations.' },
          { title: 'The commercial bet has not started', text: 'No paying customers yet. The entire remaining risk is commercial, and the plan is built around removing it in the cheapest possible order: €39 at a time.' },
          { title: 'The order is the strategy', text: 'Small business first, hand-onboarded design partners in the appointment verticals, then the vertical that retains, then agency partners — with every hire and every raise gated on paying accounts.' },
          { title: 'The destination is a standard, not a niche', text: 'The agent layer small businesses reach their other software through — embedded in the platforms they already pay for, or replacing the thin tools where the interface *was* the product. In no modelled number here.' },
        ],
      },
    ],
  },
  {
    title: 'Vision, mission and positioning',
    blocks: [
      {
        kind: 'matrix',
        head: ['', 'Statement'],
        rows: [
          ['Vision', 'LLMs are the new interface to business — buttons, menus and forms give way to human-like conversation, in text or by voice'],
          ['Mission', 'Give every small business the customer conversation that only large companies could afford — answered in seconds, day or night, and finished'],
          ['Positioning — the customer line', '*The receptionist that finishes the job*'],
          ['Positioning — the investor line', '*Agentic customer communication at small-business prices, because the inference is owned*'],
        ],
        accentRows: [0],
      },
      {
        kind: 'callout',
        text: 'For **small and medium businesses** whose customers write to them — to ask, book, order or pay — **DialogMe** is the **managed conversational platform that finishes the job**: it answers from the business’s own content, books real availability, shows products, raises structured tickets and takes payment behind an explicit confirmation gate. **Unlike** chatbots that answer and hand off, and enterprise agents priced per resolution on rented frontier models, it ships the integrations already built and runs on inference it owns — so it is priced **per conversation, not per seat**, from €39 a month.',
      },
      {
        kind: 'matrix',
        head: ['DialogMe is', 'DialogMe is not'],
        rows: [
          ['A managed, multi-tenant subscription — nothing to install', 'A private, on-premises or air-gapped deployment — that is Fnk'],
          ['An agent that completes the transaction', 'A FAQ bot, or a chatbot builder the customer configures from scratch'],
          ['Priced per conversation', 'Priced per seat or per resolution'],
          ['Reached by people in messaging apps, and by AI agents over MCP', 'An integration project the owner has to run'],
        ],
      },
      {
        kind: 'text',
        text: 'The strategy that follows from this is the go-to-market section below; the ambition beyond the plan closes the document. The full statement is on [dialogme.ai/vision](https://dialogme.ai/vision).',
      },
    ],
  },
  {
    title: 'What the product is',
    blocks: [
      {
        kind: 'matrix',
        head: ['Outcome', 'What the agent does'],
        rows: [
          ['Answer', 'Product and policy questions, grounded in the business’s own documents and catalog'],
          ['Show and recommend', 'Product grids and comparisons rendered inside the conversation'],
          ['Book', 'Real availability, confirmed and written back to the calendar'],
          ['Take payment', 'Checkout in-chat, behind an explicit confirmation gate'],
          ['Escalate properly', 'Structured tickets with full context, clean human handover'],
        ],
        minWidth: '420px',
      },
      {
        kind: 'text',
        text: '**Intent → grounding → tool call → confirmation gate → transaction.** Generative UI action cards — approval gates, product grids, comparison tables, progress steppers — are what let a conversation carry a transaction without becoming a form. Five service types ship preconfigured — E-Commerce, Offline Business, Blog, Portfolio and Finance — each with the Fnk Harness pack for it, configured at onboarding rather than built by the customer.',
      },
      {
        kind: 'matrix',
        head: ['Layer', 'Integrations — bold is live today'],
        rows: [
          ['Channels', '**Telegram** · WhatsApp · Facebook · Instagram · Web widget'],
          ['Ticketing', '**DialogMe** · Jira · Zendesk'],
          ['Booking', '**Cal.com** · Calendly · Google Calendar · Acuity'],
          ['Content', '**DialogMe** · WordPress · Ghost · Sanity.io · Medium'],
          ['Subscriptions', '**Ko-fi** · **Buy Me a Coffee** · Patreon'],
          ['Catalog and payments', 'Shopify · Medusa · Saleor · Commerce Layer · Stripe · PayPal · BitPay · Coinbase'],
          ['Inference', '**Fnk stack, operated by DialogMe** · approved partner endpoints on higher plans'],
        ],
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'Stated plainly',
        text: '**Shop and Payment are the two that close the revenue loop, and both are still in build.** Every service is also an MCP endpoint, so external AI agents can transact with the business directly — the businesses reachable that way are the ones that will get used. DialogMe does not train models, does not sell a toolkit, and does not sell private deployments: a business that needs one is referred to Fnk.',
      },
    ],
  },
  {
    title: 'Built on Fnk — owned inference',
    blocks: [
      {
        kind: 'text',
        text: 'DialogMe runs on its own AI stack, **Fnk** — the Harness, the agent framework and the reactive inference engine beneath the service. One stack, no per-token supplier between DialogMe and its customers.',
      },
      { kind: 'node', node: <VerticalIntegrationChart /> },
      {
        kind: 'matrix',
        head: ['Consequence', 'Why it follows', 'Where it shows up'],
        rows: [
          ['Margin holds as volume grows', 'Inference is a fixed infrastructure cost, not a per-token bill that scales with success', 'Gross margin modelled at **83–87%** as volume grows; ≈81% Starter and ≈79% Scale at the unit level'],
          ['Priced at small-business economics', 'The interaction is priced at owned capacity, not at a frontier-model API call plus the vendor’s margin', '**6×–11× cheaper** per customer interaction than published incumbent rates'],
          ['One line of accountability', 'A defect is the founder’s to fix at whichever layer it sits', 'The whole development schedule assumes it'],
          ['A private deployment exists for the account that needs one', 'Our own stack ships as a Fnk box', 'The path for a customer with a residency rule or an audit'],
        ],
        minWidth: '640px',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'Two honest limits',
        text: 'The integration **does not extend to the model** — we do not train frontier models; models are open-weight and swappable. And **a written, transferable licence for the Fnk stack is an open item**: DialogMe’s use of it carries no licence fee in the plan today, and the licence will be signed before any external investor enters.',
      },
    ],
  },
  {
    title: 'What has been measured',
    blocks: [
      {
        kind: 'text',
        text: 'The proof of concept belongs to Fnk and is documented in full at [fnk.ai](https://fnk.ai). **Measured on 21 August 2026**, paired run, identical workload, same model, same NVIDIA GPU, inference concurrency swept 4 → 64, against what a competent team builds today: blocking Spring MVC + LangChain4j + vLLM.',
      },
      {
        kind: 'matrix',
        head: ['At maximum tested concurrency (64)', 'Assembled stack', 'Fnk AI stack', 'Improvement'],
        rows: [
          ['Successful completion rate', '4.76 req/s', '**9.70 req/s**', '**2.04×**'],
          ['Weighted mean latency', '9,792 ms', '**6,225 ms**', '**−36.4%**'],
          ['Stable-stage p95 latency', '2.43× higher', '**5,237 ms**', '**−58.9%**'],
        ],
        numeric: [1, 2, 3],
      },
      { kind: 'node', node: <ConcurrencySweepChart /> },
      {
        kind: 'text',
        text: '**For DialogMe the reading is simple: the same GPU serves about twice the conversations.** That is what lets a €39 plan carry 250 conversations at ≈81% margin. The row that matters for defensibility is the control arm: a reactive backend still talking to vLLM over HTTP barely helped — the gain appears only when capacity signalling, admission and cancellation continue *through* the inference boundary.',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'The honest boundary',
        text: 'A whole-stack result, one run per point, engineering evidence rather than an SLA, with a visible and unexplained first-stage order effect — and **not yet independently reproduced**. Independent reproduction is the first thing Fnk’s certification budget buys.',
      },
    ],
  },
  {
    title: 'Business model and unit economics',
    blocks: [
      {
        kind: 'matrix',
        head: ['Plan', 'Price / mo', 'Conversations', 'Rate', 'Margin'],
        rows: [
          ['Free', '€0', '50 in total', '—', '—'],
          ['Starter', '€39', '250', '€0.156', '≈81%'],
          ['Scale', '€299', '3,500', '€0.085', '≈79%'],
          ['Custom', 'Sales', 'Unlimited', 'Contracted', 'Deal'],
        ],
        accentRows: [1, 2],
        numeric: [1, 2, 3, 4],
      },
      { kind: 'node', node: <CostComparisonChart /> },
      {
        kind: 'cards',
        cols: 2,
        items: [
          { title: 'Why not per seat', text: 'Per-seat pricing punishes exactly the customers this is for — a five-person clinic with thousands of conversations, or a fifty-person firm where four people ever open the console.' },
          { title: 'All of it recurs', text: 'Every euro is subscription revenue, from the first month, monthly cancellable.' },
          { title: 'The number to interrogate', text: 'Blended ARPU rising **€45 → €60** is the mix moving onto Scale. A Starter-only base cuts Year-5 revenue by **35%** and takes cash below zero.' },
          { title: 'The cost base it has to cover', text: 'Development costs **€22,076** in year one and **€17,976** a year thereafter, with no founder salary in it. **Roughly 110–140 paying accounts cover the entire development cost base.**' },
        ],
      },
    ],
  },
  {
    title: 'Market',
    blocks: [
      {
        kind: 'metrics',
        items: [
          { value: '≈130,000', label: 'Spanish ideal-customer profile, filtered from INE DIRCE', tone: 'plain' },
          { value: '≈1.1M', label: 'EU extension, same filters', tone: 'plain' },
          { value: '≈660', label: 'Year-5 base-case paying accounts' },
          { value: '≈0.05%', label: 'Share of the EU frame the base case needs' },
        ],
      },
      {
        kind: 'text',
        text: 'Spain alone counts roughly 11,775 hair salons and 13,718 beauty salons; the base case’s Year-5 accounts are under 3% of that one vertical. Spanish firms with 10–49 employees sit at **8.7%** AI adoption against **49.2%** for large firms — **the gap is the market.**',
      },
      {
        kind: 'cards',
        items: [
          { title: 'The buyer is becoming a machine', text: 'MCP and agent-to-agent protocols let one system discover what another can do and transact with it directly.' },
          { title: 'Regulation became a feature', text: 'EU AI Act transparency obligations apply from **2 August 2026**. A service that produces its own disclosure and audit trail is a requirement.' },
          { title: 'The category is being bought', text: 'Salesforce paid **$3.6bn** for Fin. Sierra raised at **$15bn** on roughly $150m ARR. HubSpot halved its resolution price — all above our price point.' },
        ],
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'The honest limit',
        text: 'Every filter is a management assumption on an official frame. The constraint is not the market’s size; it is whether the funnel converts and retains — and there is no primary customer research yet.',
      },
    ],
  },
  {
    title: 'Strategy and go-to-market — small business first',
    blocks: [
      {
        kind: 'text',
        text: '**This is the part of the plan that is a decision rather than a forecast, and it is the part we would most want an investor to interrogate.** Small businesses decide in days, will test something unproven if it is cheap, produce the reference material, generate the production workload the benchmark is measured against, surface every defect at €39 rather than inside a six-figure deployment, and fund operations from the first paying month.',
      },
      {
        kind: 'matrix',
        head: ['Strategic choice', 'What it means'],
        rows: [
          ['Win one wedge before widening', 'Appointment businesses on Telegram first, booking onto their own calendar — the return is countable and the owner decides in one conversation. E-commerce once Shop and Payment ship'],
          ['Small business first', 'One person decides, in days; they will try something unproven; they produce the named customers and case study every later channel asks for'],
          ['Own the inference, price the conversation', 'DialogMe runs on the Fnk stack, so inference is fixed infrastructure — the same GPU serves about twice the conversations and the margin holds as volume grows'],
          ['Self-serve is the product', 'Signup to a working assistant with no human involved; a question asked twice is a product defect, not a staffing problem'],
          ['Earn every step with paying accounts', 'No hire, no channel spend and no raise ahead of the evidence that justifies it — the gates below'],
        ],
        minWidth: '560px',
      },
      {
        kind: 'matrix',
        head: ['Phase', 'What it is'],
        rows: [
          ['1 · Design partners and self-serve', 'Hand-onboarded partners in the appointment verticals — salons, clinics, studios, tutors — on weekly loops; ten-minute self-serve onboarding'],
          ['2 · Concentrate on the vertical that retains', 'Associations, chambers and Kit Digital agents for the vertical that takes more than 40% of signups'],
          ['3 · Agency partners', 'Once a non-founder can onboard a business unassisted; agencies that build one-off bots bring recurring accounts'],
        ],
      },
      {
        kind: 'matrix',
        head: ['Paying accounts', 'What it buys'],
        rows: [
          ['≈10 live', 'Self-serve works unassisted; the first real retention signal'],
          ['≈100 paying', 'Two measured cohorts and a case study — **the company stops consuming founder capital**, and the seed conversation opens'],
          ['≈500–1,000', 'A reference list rather than a reference customer, vertical proof points, retention history a partner will accept'],
        ],
        accentRows: [1],
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'The support trap, and the rule that prevents it',
        text: 'Support is urgent and building is important, so the urgent work wins every day. Three rules: self-serve is the product and support volume is a **product defect signal**; a published next-business-day response window that is honoured; founder support time capped, with overflow going to contracted hours funded from the first €100,000 banked.',
      },
    ],
  },
  {
    title: 'Competition',
    blocks: [
      {
        kind: 'matrix',
        head: ['Company', 'Answers', 'Acts', 'Your customers', 'Priced for SMBs', 'Owns inference'],
        rows: [
          ['DialogMe', '●', '●', '●', '●', '● (Fnk)'],
          ['Sierra, Decagon, Fin', '●', '●', '●', '✖', '✖'],
          ['Tidio, ManyChat, Chatbase', '◐', '◐', '●', '●', '✖'],
          ['Botpress, Voiceflow', '●', '◐', '●', '◐', '✖'],
          ['Chatwoot (self-hosted)', '◐', '✖', '●', '●', '✖'],
        ],
        accentRows: [0],
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'Where DialogMe should not compete',
        text: 'Businesses standardised on HubSpot, Zendesk or Salesforce; WhatsApp-only buyers until the channel ships; teams that want to build their own agent. **The weakest published comparison is the entry tier** — Chatbase at $19 is cheaper than €39, and the plan says so.',
      },
    ],
  },
  {
    title: 'Financial scenarios',
    blocks: [
      {
        kind: 'text',
        text: '**The scenarios are defined by how long development takes, not by how fast the product then sells.** Development is to a deliberately reduced Phase 1 — managed platform, billing, payments — sized at 105 identified person-days.',
      },
      {
        kind: 'matrix',
        head: ['Scenario', 'Development', 'First revenue', 'Year-5 revenue', 'EBITDA-positive', 'Coverage'],
        rows: [
          ['Pessimistic', '24 months', 'Year 3 — €13,104', '€81,600', 'Not inside five years', '1.01×'],
          ['Base', '12 months', 'Year 2 — €39,420', '€409,680', 'Year 4', '1.29×'],
          ['Optimistic', '6 months', 'Year 1 — €38,880', '€1,367,616', 'Year 2', '2.10×'],
        ],
        accentRows: [1],
        numeric: [3, 5],
        minWidth: '640px',
      },
      {
        kind: 'text',
        text: 'Development is funded by three **€50,000** founder rounds — one a year, paid in regardless of revenue, nothing ever distributed. **€0 of external funding is required in any scenario — but in the pessimistic case, only just:** it ends Year 5 with **€1,515** of the €150,000 left. **The eighteen months between best and worst are worth €1,286,016 of Year-5 revenue.**',
      },
      {
        kind: 'metrics',
        items: [
          { value: '€0.8–1.5M', label: 'Seed modelled, base column — once ≈100 accounts pay' },
          { value: '€4–6M', label: 'Pre-money valuation', tone: 'plain' },
          { value: '15–22%', label: 'Implied dilution', tone: 'plain' },
          { value: '18–24 mo', label: 'Runway at the resulting cost base', tone: 'plain' },
        ],
      },
      {
        kind: 'callout',
        title: 'Not yet — and the exception, stated in advance',
        text: 'The recommendation of record is not to raise during development. Use of a seed: engineering 40% (**Shop and Payment first**, then WhatsApp and the web widget), go-to-market 35%, support 10%, security, working capital and reserve 15%. **If the first market year closes with fewer than fifty paying accounts, a €200,000–€400,000 pre-seed becomes the prudent move rather than the premature one.**',
      },
    ],
  },
  {
    title: 'Defensibility',
    blocks: [
      {
        kind: 'cards',
        items: [
          { title: 'Owned inference, one layer down', text: 'The Fnk stack is why the margin holds and why the price is possible. A competitor renting a model API cannot match it without changing its cost structure.' },
          { title: 'The integration surface', text: 'Channels, ticketing, booking, content, subscriptions, catalog and payments — maintained, versioned and supported. Unglamorous, endless, and where switching cost accumulates.' },
          { title: 'Preconfigured service types', text: 'Five Harness packs configured at onboarding. The customer buys the outcome, not a toolkit.' },
          { title: 'Confirmation gates and the evidence trail', text: 'Nothing that costs money executes without explicit approval, and every transaction leaves a trace — the AI Act disclosure and audit requirement, built in.' },
          { title: 'Installed-base switching cost', text: 'Knowledge indexes, integration configuration, conversation history, evaluation sets — accumulated per customer.' },
          { title: 'MCP from day one', text: 'Every service is reachable by other agents, not just by people — the businesses that are reachable are the ones that get used.' },
        ],
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'The honest counterweight',
        text: 'None of this is impossible to replicate, and the entry tier is contested by cheaper tools. The barrier is the combination — acting, not answering; priced for a small business; on owned inference — and it only holds while the integration surface and the account base are genuinely accumulating. The intellectual property in the stack itself is documented at fnk.ai.',
      },
    ],
  },
  {
    title: 'The ambition, and the gap to it',
    blocks: [
      {
        kind: 'text',
        text: 'The modelled exit is a small, profitable European SaaS company — **€2m–€14m** at Year 5 depending on whether a buyer sees a flat base or a retaining, AI-native one. That is a plan, with a schedule and a €150,000 downside to find out it was wrong. Beyond it sits one standard position: **become the agent layer, and take the market the interface used to hold.** A small business today rents a stack of horizontal SaaS — a site builder, a booking tool, a catalog, a helpdesk — and every one of them is a database with a user interface on top, and the interface is the part that is ceasing to be the product.',
      },
      {
        kind: 'matrix',
        head: ['Route', 'What it means', 'Who it applies to'],
        rows: [
          ['Embed', 'The platform ships DialogMe agents inside its own product — revenue share, our service under their brand', 'Large horizontals with distribution we cannot buy'],
          ['Intermediate', 'The platform stays, but the business and its customers reach it *through* our agent over MCP and A2A', 'Anything with an API, which is all of them'],
          ['Replace', 'For the thinnest tools — where a form and a table *were* the whole product — the agent simply does the job', 'Booking pages, simple helpdesks, single-purpose SMB tools'],
        ],
        minWidth: '560px',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'What has to be true, and what we have',
        text: 'A horizontal platform decides embedding our agent beats building one, or enough SMBs decide the conversation replaces the dashboard. We have a working product, no platform agreement, and no evidence yet that an SMB will cancel a subscription over it. **This claim is priced into no figure on this page, and it would take most of a decade.** The mirror-image ambition — the inference layer a data centre standardises on — is Fnk’s, at fnk.ai.',
      },
    ],
  },
  {
    title: 'Risks, stated plainly',
    blocks: [
      {
        kind: 'matrix',
        head: ['Risk', 'Why it is real', 'What reduces it'],
        rows: [
          ['Zero paying customers today', 'The whole commercial thesis is untested', 'Phase 1 tests it at €39 a time, with hand-onboarded design partners before any paid acquisition'],
          ['Time to market', 'Eighteen months of difference is worth €1,286,016 of Y5 revenue', 'A deliberately reduced Phase 1 of 105 person-days; the enterprise items moved to Fnk'],
          ['The worst case is thin', 'The pessimistic case ends Year 5 with €1,515 of the founder’s €150,000', 'The pre-seed exception at under fifty accounts, written down in advance'],
          ['The Scale mix', 'A Starter-only base cuts Y5 revenue 35% and takes cash below zero', 'Outcome pricing and dedicated inference as the reasons to move up'],
          ['Shop and Payment are not built', 'The two integrations that close the revenue loop are still in build', 'First on the engineering roadmap; the first use of any seed'],
          ['The stack licence is not yet written', 'DialogMe’s use of the Fnk stack has no signed licence today', 'A written, transferable licence before any external investor enters'],
          ['One founder, platform and stack', 'Concentration risk', 'The cost base at its floor; every hiring trigger written down'],
          ['The entry tier is contested', 'Chatbase at $19 is cheaper than €39', 'The comparison is on acting, not answering — and the plan says the weakness out loud'],
          ['No primary customer research yet', 'Market sizing is desk research on an official frame', 'The largest open item in the plan; design partners are the first answer'],
        ],
        minWidth: '640px',
      },
    ],
  },
];
